import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useFetchMappings,
  useCreateMapping,
  useUpdateMapping,
  useParseCSV,
  savedMappingToColumnMappings,
  columnMappingsToSavedMapping,
  getMissingRequiredFields,
} from "byzantine/src/dbbl/businessLogic/entities/achImport";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { useACHImportContext } from "../ACHImportContext";
import { getAchImportDefaultAction } from "./achImportDefaults";
import { ACH_FIELDS } from "./columnMappingConstants";
import ColumnMappingScreen from "./ColumnMappingScreen";

const extractSavedMappings = (data: unknown): API.ACHImport.CSVFieldMapping[] =>
  (data as { results: API.ACHImport.CSVFieldMapping[] })?.results ?? [];

const PARSE_WARNING_FIELDS: Partial<Record<string, API.ACHImport.AchField>> = {
  unrecognized_action: "action",
  unrecognized_account_type: "account_type",
};

const ACHColumnMappingScreen = () => {
  const navigate = useNavigate();
  const features = useUserFeatures();
  const { csvResponse, setParsedCsvPaymentsResponse } = useACHImportContext();
  const defaultAction = getAchImportDefaultAction(features);
  const fields = useMemo(
    () =>
      ACH_FIELDS.map((field) =>
        field.field === "action"
          ? { ...field, defaultNoteArgs: { action: defaultAction } }
          : field
      ),
    [defaultAction]
  );

  const onParseSuccess = useCallback(
    (result: API.ACHImport.CSVParseResponse) => {
      setParsedCsvPaymentsResponse(result);
      const hasNewRecipients = result.rows.some(
        (row) => row.non_recipient_destination !== null
      );
      navigate(
        hasNewRecipients ? "../review-recipients" : "../additional-details"
      );
    },
    [setParsedCsvPaymentsResponse, navigate]
  );

  return (
    <ColumnMappingScreen<
      API.ACHImport.AchField,
      API.ACHImport.ColumnMapping,
      API.ACHImport.CSVFieldMapping,
      API.ACHImport.CSVParseResponse,
      API.ACHImport.CSVFieldMappingRequest
    >
      titleKey="column-mapping-title"
      backPath=".."
      fields={fields}
      csvResponse={csvResponse}
      hooks={{
        useFetchMappings,
        useCreateMapping,
        useUpdateMapping,
        useParseCSV,
        savedMappingToColumnMappings,
        columnMappingsToSavedMapping,
        getMissingRequiredFields,
        extractSavedMappings,
      }}
      parseWarningFields={PARSE_WARNING_FIELDS}
      parseWarningContextOverrides={{
        unrecognized_action: { action: defaultAction },
      }}
      onParseSuccess={onParseSuccess}
    />
  );
};

export default ACHColumnMappingScreen;
